import type {
  AccessVersionResponse,
  GetResult,
  SessionAccessProfile,
} from "@prolarus/shared";
import { ApiError, apiGet } from "@/lib/apiClient";
import { tryGet, toQueryString } from "./_shared";

export type AccessDeniedReason =
  | "NO_MODULE_ACCESS"
  | "FEATURE_NOT_LICENSED"
  | "FEATURE_NOT_ENABLED"
  | "NO_PERMISSION";

export interface AccessCheckResult {
  allowed: boolean;
  reason?: AccessDeniedReason;
  message?: string;
}

export type { SessionAccessProfile };

function toError(err: unknown): Error {
  if (err instanceof ApiError) return err;
  if (err instanceof Error) return err;
  return new Error(String(err));
}

// Mirrors the prior client-side accessControlService surface. The composer
// logic now runs server-side: /session collapses 3 calls into 1, and /check
// runs the two-gate evaluation in-process. _companyId is preserved for
// signature compat; the route clamps company_id from the JWT.
export const accessControlService = {
  // canUserPerformAction throws on transport errors (matching the prior
  // service which threw on first underlying error). On success it returns
  // AccessCheckResult directly — the wrapper does not envelope it.
  async canUserPerformAction(
    profileId: string,
    _companyId: string,
    moduleCode: string,
    permissionCode: string | string[],
    featureCode?: string,
  ): Promise<AccessCheckResult> {
    const codes = Array.isArray(permissionCode)
      ? permissionCode.join(",")
      : permissionCode;
    const params: Record<string, string> = {
      profile_id: profileId,
      module_code: moduleCode,
      permission_code: codes,
    };
    if (featureCode) params.feature_code = featureCode;
    try {
      const result = await apiGet<{ data: AccessCheckResult }>(
        `/api/access-control/check${toQueryString(params)}`,
      );
      return result.data;
    } catch (err) {
      throw toError(err);
    }
  },

  resolveSessionAccess(
    profileId: string,
    _companyId: string,
  ): Promise<GetResult<SessionAccessProfile>> {
    return tryGet<SessionAccessProfile>(
      `/api/access-control/session${toQueryString({ profile_id: profileId })}`,
    );
  },

  getVersion(profileId: string): Promise<GetResult<AccessVersionResponse>> {
    return tryGet<AccessVersionResponse>(
      `/api/access-control/version${toQueryString({ profile_id: profileId })}`,
    );
  },
};
